import { useRef, useState } from 'react';

import type { Editor } from '@tiptap/react';

import type { CommunityPostComposerProps } from '../CommunityPostComposer.type';
import { DEFAULT_UPLOAD_ACCEPT, DEFAULT_UPLOAD_MAX_BYTES, tr } from './constants';
import { landOnTextblock } from './landOnTextblock';
import { isUploadingImageSrc } from './nodes/Image/isUploadingImageSrc';

type UploadImage = NonNullable<CommunityPostComposerProps['onUploadImage']>;
type UploadedImage = NonNullable<Awaited<ReturnType<UploadImage>>>;

interface ImagePasteOptions {
  editor: Editor | null;
  onUploadImage?: UploadImage;
  disabled?: boolean;
  accept?: string;
  maxBytes?: number;
  translations?: Record<string, string>;
}

// Office apps copy text together with a picture of it; that paste is text, not an image.
const RICH_TEXT_TYPES = ['text/plain', 'text/html', 'text/rtf'];

export const useImagePaste = ({
  editor,
  onUploadImage,
  disabled = false,
  accept = DEFAULT_UPLOAD_ACCEPT,
  maxBytes = DEFAULT_UPLOAD_MAX_BYTES,
  translations,
}: ImagePasteOptions) => {
  const [error, setError] = useState<string | null>(null);
  // Preview URL to finished upload, null while it uploads. Kept after finishing so a redo still resolves.
  const uploads = useRef(new Map<string, UploadedImage | null>());

  // Swaps finished uploads onto their hosted URL and removes images with no upload behind them.
  const syncUploads = () => {
    if (!editor || editor.isDestroyed) return;

    const transaction = editor.state.tr;
    editor.state.doc.descendants((node, position) => {
      if (node.type.name !== 'image' || !isUploadingImageSrc(node.attrs.src)) return;

      const upload = uploads.current.get(node.attrs.src);
      if (upload === null) return;

      const mapped = transaction.mapping.map(position);
      if (upload) {
        const attributes = {
          ...node.attrs,
          src: upload.url,
          alt: upload.alt ?? node.attrs.alt,
          blobKey: upload.blobKey,
        };
        transaction.setNodeMarkup(mapped, undefined, attributes);
      } else {
        transaction.delete(mapped, mapped + node.nodeSize);
      }
    });

    // Undo must not step back onto a preview URL that no longer resolves.
    if (transaction.docChanged) editor.view.dispatch(transaction.setMeta('addToHistory', false));
  };

  const handlePaste = (event: ClipboardEvent): boolean => {
    const clipboard = event.clipboardData;
    if (!editor || !onUploadImage || disabled || !clipboard) return false;

    const acceptedTypes = accept.split(',');
    const images = Array.from(clipboard.files).filter((file) => acceptedTypes.includes(file.type));
    if (images.length === 0 || RICH_TEXT_TYPES.every((type) => clipboard.types.includes(type))) return false;

    const uploadable = images.filter((file) => file.size <= maxBytes);
    const sizeError =
      uploadable.length < images.length ? tr(translations, 'community.composer.upload_file_too_large') : null;
    if (uploadable.length === 0) {
      setError(sizeError);
      return true;
    }

    const nodes = uploadable.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      uploads.current.set(previewUrl, null);

      onUploadImage(file)
        .catch(() => null)
        .then((upload) => {
          if (upload) {
            uploads.current.set(previewUrl, upload);
          } else {
            uploads.current.delete(previewUrl);
            setError(tr(translations, 'community.composer.upload_error'));
          }
          syncUploads();
          URL.revokeObjectURL(previewUrl);
        });

      return { type: 'image', attrs: { src: previewUrl, alt: file.name } };
    });

    const { from, to } = editor.state.selection;
    editor.chain().focus().insertContentAt({ from, to }, nodes).command(landOnTextblock).run();
    // Set after the insert, whose editor update clears the error.
    setError(sizeError);
    return true;
  };

  const clearError = () => setError(null);

  return { error, clearError, handlePaste, syncUploads };
};

export type ImagePasteControls = ReturnType<typeof useImagePaste>;
